// instrumentation-client.ts — captura erros do navegador em produção e manda
// pro servidor (POST /api/observability/client-error), pra ver o texto
// completo de erros de hidratação do React (#418/#423/#425) que em produção
// só aparecem minificados ("Minified React error #418; visit ... for the full
// message"). Em desenvolvimento o React já mostra a mensagem longa, então
// isso só importa em produção — mas o listener roda em todo ambiente (é
// barato e não faz mal nenhum em dev).
//
// Next.js 16 carrega este arquivo automaticamente no bundle do cliente, bem
// no início, antes de qualquer outro código da aplicação (ver docs
// "instrumentation-client"). Nada de import de módulos server-only aqui.

const MAX_LEN = 2000;
const MAX_POR_MINUTO = 5;
const ENDPOINT = "/api/observability/client-error";

type TipoEvento =
  | "window.error"
  | "unhandledrejection"
  | "react.hydration"
  | "console.error";

interface PayloadClientError {
  tipo: TipoEvento;
  mensagem: string;
  stack: string | null;
  rota: string;
  ua: string;
  viewport: string;
  ts: string;
}

// Contador simples por rota, resetado a cada minuto — evita loop de erro
// (ex: um componente que rejoga o mesmo erro em cada render) inundando o
// endpoint e o console.error do runtime.
const contadorPorRota = new Map<string, { minuto: number; count: number }>();

function podeEnviar(rota: string): boolean {
  const minutoAtual = Math.floor(Date.now() / 60_000);
  const atual = contadorPorRota.get(rota);
  if (!atual || atual.minuto !== minutoAtual) {
    contadorPorRota.set(rota, { minuto: minutoAtual, count: 1 });
    return true;
  }
  if (atual.count >= MAX_POR_MINUTO) return false;
  atual.count += 1;
  return true;
}

function truncar(valor: string | null | undefined): string | null {
  if (!valor) return null;
  return valor.length > MAX_LEN ? `${valor.slice(0, MAX_LEN)}…` : valor;
}

function montarPayload(
  tipo: TipoEvento,
  mensagem: string,
  stack?: string | null,
): PayloadClientError {
  return {
    tipo,
    mensagem: truncar(mensagem) ?? "",
    stack: truncar(stack ?? null),
    rota: window.location.pathname,
    ua: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    ts: new Date().toISOString(),
  };
}

function enviar(payload: PayloadClientError): void {
  if (!podeEnviar(payload.rota)) return;
  try {
    const body = JSON.stringify(payload);
    if (typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    }
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      // best-effort — nunca deixa a captura de erro gerar outro erro visível
    });
  } catch {
    // idem — silencioso de propósito
  }
}

// Mensagens de hidratação/erro minificado do React que valem a pena
// registrar com prioridade — o próprio #418/#423/#425 e a versão longa
// "Hydration failed because..." quando o build ainda não está minificado.
const PADRAO_REACT_ERRO =
  /Minified React error #(418|423|425)|Hydration failed because|There was an error while hydrating|Text content does not match server-rendered HTML/i;

function extrairStack(error: unknown): string | null {
  if (error instanceof Error) return error.stack ?? null;
  return null;
}

function extrairMensagem(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

window.addEventListener("error", (event: ErrorEvent) => {
  const mensagem = event.message || extrairMensagem(event.error);
  const stack = extrairStack(event.error);
  const tipo: TipoEvento = PADRAO_REACT_ERRO.test(mensagem)
    ? "react.hydration"
    : "window.error";
  enviar(montarPayload(tipo, mensagem, stack));
});

window.addEventListener(
  "unhandledrejection",
  (event: PromiseRejectionEvent) => {
    const mensagem = extrairMensagem(event.reason);
    const stack = extrairStack(event.reason);
    const tipo: TipoEvento = PADRAO_REACT_ERRO.test(mensagem)
      ? "react.hydration"
      : "unhandledrejection";
    enviar(montarPayload(tipo, mensagem, stack));
  },
);

// Intercepta console.error pra pegar a mensagem que o React imprime sobre
// hydration/minificado — muitos desses casos NÃO disparam window "error"
// (o React captura e loga, não deixa a exceção subir).
const consoleErrorOriginal = console.error.bind(console);
console.error = (...args: unknown[]) => {
  try {
    const texto = args
      .map((a) => (typeof a === "string" ? a : extrairMensagem(a)))
      .join(" ");
    if (PADRAO_REACT_ERRO.test(texto)) {
      enviar(montarPayload("react.hydration", texto, null));
    } else if (/^Error:|^Uncaught/.test(texto)) {
      enviar(montarPayload("console.error", texto, null));
    }
  } catch {
    // nunca deixa a instrumentação quebrar o console.error real
  }
  consoleErrorOriginal(...args);
};
